import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { useEffect, useState } from "react";
import { updateInspectionAdjustedGrades, fetchInspectionData, fetchRecalculateAdjustedGrades } from "../../store/thunks/inspectionThunks";
import { selectInspection } from "../../store/selectors/inspectionSelectors";
import { InspectionGradeInfo } from "../Inspection/InspectionGradeInfo";
import { Button } from "primereact/button";
import { AdjustableGrade } from "./AdjustableGrade";
import "./AdjustedGradeEditor.css";
import { Grade } from "../../types/inspection";
import { adjustedGradesDto, combineAdjustedGrades, GRADE_TYPE_CONDITION, GRADE_TYPE_MANAGEMENT } from "../../utils/GradeUtils";

export const AdjustedGradeEditor = () => {
    const dispatch = useAppDispatch();
    const { inspection, loading, adjustedGrades: existingAdjustedGrades, errorAdjustedGrades, loadingAdjustedGrades } = useAppSelector(selectInspection);
    const [adjustedIdwalGrade, setAdjustedIdwalGrade] = useState<number | null>(0);
    const [adjustedConditionGrade, setAdjustedConditionGrade] = useState<number | null>(0);
    const [adjustedManagementGrade, setAdjustedManagementGrade] = useState<number | null>(0);
    const [adjustedGrades, setAdjustedGrades] = useState<Grade[] | null>([]);
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);

    const { inspectionId } = useParams();

    useEffect(() => {
        if (inspectionId && !inspection) {
            dispatch(fetchInspectionData(inspectionId));
        }
    }, [inspectionId]);

    useEffect(() => {
        if (inspection && !loadingAdjustedGrades) {
            const combinedGrades = combineAdjustedGrades(inspection?.grades ?? [], existingAdjustedGrades?.adjustedGrades ?? []);
            setAdjustedGrades(combinedGrades);

            if (existingAdjustedGrades?.adjustedGrades?.length) {
                setAdjustedIdwalGrade(existingAdjustedGrades.calculatedGrades.overallGrade);
                setAdjustedConditionGrade(existingAdjustedGrades.calculatedGrades.conditionGrade.computedConditionGrade);
                setAdjustedManagementGrade(existingAdjustedGrades.calculatedGrades.managementGrade.computedManagementGrade);
            } else {
                setAdjustedIdwalGrade(inspection?.overallScore ?? 0);
                setAdjustedConditionGrade(inspection?.conditionScore ?? 0);
                setAdjustedManagementGrade(inspection?.managementScore ?? 0);
            }
        }
    }, [existingAdjustedGrades, loadingAdjustedGrades]);

    const titlesBarMain = () => {
        return (
            <div className="col-12 mt-4">
                {titlesBarHeaders()}
            </div>
        )
    }

    const titlesBarSub = () => {
        return (
            <div className="col-12 lg:col-6 mt-4">
                {titlesBarHeaders()}
            </div>
        )
    }

    const titlesBarHeaders = () => {
        return (
            <div className="flex justify-content-between align-items-center">
                <div className="small-text ml-5">Grading areas</div>
                <div className="flex gap-8 mr-7">
                    <span className="small-text">Original</span>
                    <span className="small-text ml-6">Adjusted</span>
                </div>
            </div>
        )
    }

    const onGradeAdjust = (score: number, vesselGradeId: number) => {
        if (saveDisabled) {
            setSaveDisabled(false);
        }

        if (!inspectionId || !adjustedGrades?.length) {
            console.warn("No adjusted grades to save");
            return;
        }

        const newAdjustedGrades = adjustedGrades?.map(grade => {
            if (grade.vesselGradeId === vesselGradeId) {
                return {
                    ...grade,
                    adjustedPoint: score / 10
                }
            }
            return grade;
        });

        const payloadBody = adjustedGradesDto(newAdjustedGrades);
        dispatch(fetchRecalculateAdjustedGrades(inspectionId, payloadBody));
    }

    const saveAdjustedGrades = async () => {
        setSaveDisabled(true);

        if (!inspectionId || !adjustedGrades?.length) {
            console.warn("No adjusted grades to save");
            return;
        }

        const payloadBody = adjustedGradesDto(adjustedGrades);
        dispatch(updateInspectionAdjustedGrades(inspectionId, payloadBody));
    }

    const editableGrades = (type: number) => {
        if (!inspection?.grades) {
            return null;
        }

        return adjustedGrades?.filter(grade => grade.type === type && grade.point !== null).map(grade => {
            const originalGrade = grade.point * 10;
            const adjustedGrade = (grade.adjustedPoint ?? grade.point) * 10;
            return (
                <div key={grade.vesselGradeId} className="col-12 lg:col-6 grade-entry">
                    <AdjustableGrade vesselGradeId={grade.vesselGradeId} name={grade.name} originalGrade={originalGrade} adjustedGrade={adjustedGrade} adjustable onAdjust={onGradeAdjust} />
                </div>
            )
        });
    }

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!inspection?.grades && errorAdjustedGrades) {
        return <div>Error loading inspection grading data</div>
    }

    const hasAdjustableGrades = adjustedGrades?.some(grade => grade.point !== null && grade.point >= 0);

    const displayGrades = () => {
        return (
            <div className="col-12 adjustable-grades mt-4">
                {titlesBarMain()}
                <div className="px-2 mb-2">
                    <AdjustableGrade name={"Idwal Grade"} originalGrade={inspection?.overallScore ?? 0} adjustedGrade={adjustedIdwalGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                <div className="px-2 mb-2">
                    <AdjustableGrade name={"Condition Grade"} originalGrade={inspection?.conditionScore ?? 0} adjustedGrade={adjustedConditionGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                <div className="px-2">
                    <AdjustableGrade name={"Management Grade"} originalGrade={inspection?.managementScore ?? 0} adjustedGrade={adjustedManagementGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                <div className="hidden lg:flex align-items-center">
                    {titlesBarSub()}
                    {titlesBarSub()}
                </div>
                <div className="block lg:hidden">
                    {titlesBarSub()}
                </div>
                <div id="sub-grade-areas" className="flex flex-wrap">
                    {editableGrades(GRADE_TYPE_CONDITION)}
                </div>
                <div id="other-grade-areas" className="flex flex-wrap mt-3">
                    {editableGrades(GRADE_TYPE_MANAGEMENT)}
                </div>
                <div className="flex justify-content-end mt-3 mr-3 mb-3">
                    <Button label="Save changes" disabled={saveDisabled} onClick={saveAdjustedGrades}/>
                </div>
            </div>
        )
    }

    const displayNoGrades = () => {
        if (loadingAdjustedGrades) {
            return <div>Loading...</div>;
        }

        return (
            <div className="col-12 adjustable-grades mt-4">
                <div className="flex align-items-center justify-content-center m-8">
                    <span className="font-semibold">No grades to adjust</span>
                </div>
            </div>
        )
    }

    return (
        <div className="">
            <div id="adjusted-grading-editor-header">
                <div id="adjusted-grading-editor-header-title" className="flex align-items-center justify-content-between mb-4 ml-2">
                    <h2>Adjusted Grade Editor</h2>
                </div>
            </div>
            <InspectionGradeInfo />
            {hasAdjustableGrades ? (displayGrades()) : (displayNoGrades())}
        </div>
    )
}